import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { SalesReportResult } from "@shared-core/data/adapter";
import type { MarketingCategory } from "@shared-core/data/api-types";
import {
  formatCurrencyGbpCompact,
  formatCount,
} from "@shared-core/domain/formatters";
import {
  fetchCustomerSuggestions,
  gatherSuggestions,
  type SearchSuggestion,
} from "@shared-core/domain/searchSuggestions";
import {
  DateRangePicker,
  type DateRangePreset,
} from "../app/components/patterns/DateRangePicker";
import { SearchTypeahead } from "../app/components/patterns/SearchTypeahead";
import { StatCard } from "../app/components/patterns/StatCard";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import type { DateRange } from "react-day-picker";
import { format } from "date-fns";

// ReportsPage follows the canonical composed-view pattern: the backend does
// the filtering/aggregation (GET /views/sales-report, over
// fct_marketing_booking), the page just collects filters and renders the
// result. No client-side computation of the number.

const CATEGORY_LABEL: Record<MarketingCategory, string> = {
  print: "Print",
  display: "Wide format",
  mailing: "Mailing",
  merch: "Merchandise",
  clothing: "Clothing",
  creative: "Creative",
  other: "Other",
};
const CATEGORIES = Object.keys(CATEGORY_LABEL) as MarketingCategory[];

function toIsoDate(d: Date | undefined): string | undefined {
  return d ? format(d, "yyyy-MM-dd") : undefined;
}

export function ReportsPage() {
  const dataAdapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") || undefined;
  const [scopedCustomerName, setScopedCustomerName] = useState<string | null>(
    null,
  );

  const [category, setCategory] = useState<MarketingCategory | "">("");
  const [datePreset, setDatePreset] = useState<DateRangePreset>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");

  const [result, setResult] = useState<SalesReportResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerFilter) {
      setScopedCustomerName(null);
      return;
    }
    let cancelled = false;
    dataAdapter
      .getCustomerById(customerFilter)
      .then((detail) => {
        if (!cancelled) setScopedCustomerName(detail?.customer?.name ?? null);
      })
      .catch(() => {
        if (!cancelled) setScopedCustomerName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [customerFilter, dataAdapter]);

  const fetchSuggestions = useCallback(
    (q: string) =>
      gatherSuggestions([fetchCustomerSuggestions], dataAdapter, q),
    [dataAdapter],
  );

  const selectSuggestion = useCallback(
    (s: SearchSuggestion) => {
      setSearchQuery("");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("customer", s.id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleDismissCustomerScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dataAdapter
      .getSalesReport({
        customerId: customerFilter,
        category: category || undefined,
        dateFrom: toIsoDate(dateRange?.from),
        dateTo: toIsoDate(dateRange?.to),
      })
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch((error) => {
        console.error("Failed to load sales report:", error);
        if (!cancelled) setResult(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, customerFilter, category, dateRange]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl">Sales report</h1>
        <p className="text-sm text-muted-foreground">
          Pick a customer, a category, and a date range to get revenue and order
          count for that combination.
        </p>
      </div>

      <div className="space-y-3">
        {customerFilter && (
          <CustomerScopeChip
            customerName={scopedCustomerName}
            onDismiss={handleDismissCustomerScope}
          />
        )}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <SearchTypeahead
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search for a customer (leave blank for all my customers)..."
            ariaLabel="Search customers"
            fetchSuggestions={fetchSuggestions}
            onSelectSuggestion={selectSuggestion}
            className="flex-1 max-w-md"
          />
          <select
            value={category}
            onChange={(e) =>
              setCategory(e.target.value as MarketingCategory | "")
            }
            aria-label="Filter by category"
            className="px-3 py-1 rounded border border-border bg-card text-sm h-9 w-full sm:w-48"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
          <DateRangePicker
            date={dateRange}
            onDateChange={setDateRange}
            preset={datePreset}
            onPresetChange={setDatePreset}
            className="w-full sm:w-[280px]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          label="Revenue"
          value={
            loading || !result ? "—" : formatCurrencyGbpCompact(result.revenue)
          }
        />
        <StatCard
          label="Orders"
          value={loading || !result ? "—" : formatCount(result.orderCount)}
        />
      </div>
    </div>
  );
}
